import CSVUploadAndPreviewDialog from "./CSVUploadAndPreviewDialog";

import Seed from "../../utilities/Seed";
import Utility from "../../utilities/Utility";

export default class CompoundIntentsCSV extends CSVUploadAndPreviewDialog {
    /**
     * Dialog for compound intents CSV upload and preview
     * @module CompoundIntentsCSV
     * @param {Object} props React render
     */
    constructor(props) {
        super(props);

        this.getValidatedRow = this.getValidatedRow.bind(this);
        this.onDetectInvalidRows = this.onDetectInvalidRows.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
    }

    /**
     * Validates each field on the uploaded CSV file making sure that each row
     * has the proper value based on the provided header.
     * This is called from the parent component.
     * @param {object} row The row to evaluate.
     * @param {string} header The header to evaluate the row against.
     * @returns {object}
     */
    getValidatedRow(row, header) {
        const rowValue = row[header];
        let validated = {};

        if (["FromIntent1", "FromIntent2", "ToIntent"].includes(header)) {
            if (rowValue === "") {
                validated = { value: "Cannot be empty", class: "red" };
            } else {
                const intentIdx = window.apm.serialized.components.intents.findIndex(intent => intent.name === rowValue);
                if (intentIdx === -1) {
                    const uppercaseValue = rowValue.toUpperCase().replace(/ /g, "_");
                    const isUppercaseValid = !!uppercaseValue.match(/^[A-Z][A-Z0-9_]*$/);

                    if (isUppercaseValid) {
                        validated = { value: uppercaseValue, class: "blue" };
                    } else if (!isUppercaseValid) {
                        validated = { value: uppercaseValue, class: "red" };
                    } else {
                        validated = { value: rowValue, class: "black" };
                    }
                } else {
                    validated = { value: rowValue, class: "black" };
                }
            }
        } else {
            validated = { value: "Invalid column", class: "red" };
        }

        return validated;
    }

    /**
     * Activated when a row with invalid data is detected.
     * This is called from the parent component.
     */
    onDetectInvalidRows() {
        window.apm.notification.post('error', window.l.notification.fix_csv_errors);

        // Disable the modal submit button (this method is defined on the parent component).
        this.toggleSubmitButtonState(false);
    }

    /**
     * Handler for clicking on the primary action button on the modal.
     * This is called from the parent component.
     */
    onSubmit() {
        const csv = { ...this.state.parsedUploadedFile };
        const { intents } = window.apm.serialized.components;

        csv.data.forEach((row) => {
            // Make sure to create any intent that still does not exist in the app.
            for (const key in row) {
                const intentName = row[key];
                const intentIdx = intents.findIndex(intent => intent.name === intentName);

                if (intentIdx === -1) {
                    const intentDescription = row['IntentDescription'] ? row['IntentDescription'].trim() : 'Blank intent';
                    const newIntent = Seed.getBlankIntentObject(intentName, intentDescription, true);
                    window.apm.addNode(newIntent, false);
                }
            }

            // Create the new compound intent.
            const fromIntent1 = intents.find(intent => intent.name === row['FromIntent1']) || {};
            const fromIntent2 = intents.find(intent => intent.name === row['FromIntent2']) || {};
            const compoundIntentIdx = window.apm.serialized.compoundIntents.findIndex(
                intent => intent.fromIntent1 === fromIntent1.id && intent.fromIntent2 === fromIntent2.id,
            );
            if (compoundIntentIdx === -1) {
                const toIntent = intents.find(intent => intent.name === row['ToIntent']) || {};
                window.apm.serialized.compoundIntents.push({
                    id: Utility.uuid4(),
                    fromIntent1: fromIntent1.id,
                    fromIntent2: fromIntent2.id,
                    toIntent: toIntent.id,
                    createdTime: window.apm.timestamp,
                    lastLocalChange: window.apm.timestamp
                });
            }

            window.apm.setSerializedChanged();
        });

        // Re-render.
        window.apm.reactDOMComponents.render({
            name: 'IntentsWidget',
            props: { serialized: window.apm.serialized },
            $el: $('#intents'),
        });

        this.setState({
            parsedUploadedFile: '',
            originalUploadedFile: '',
        });

        window.apm.notification.post('success', window.l.notification.import_success);
        w2popup.close();
    }
}
